import { useEffect, useId, useMemo, useRef, useState } from "react";
import { AlertCircle, Info, Plus } from "lucide-react";
import type {
  MetaFieldType,
  MetaSchemaField,
} from "../../components/MetaSchemaEditor/MetaSchemaEditor";
import { Menu } from "../../components/Menu/Menu";
import { Select } from "../../components/Select/Select";
import styles from "./PlayerMetaEditor.module.css";

interface PlayerMetaEditorProps {
  /** Meta schema from games.config.profile.meta_schema. Empty → raw-only. */
  schema: MetaSchemaField[];
  /**
   * The initial / canonical meta object. Like PlayerStatsEditor, the component
   * owns its editing state after mount and is uncontrolled — the parent resets
   * it by remounting with a changing React `key`, never by feeding a new
   * `value` prop.
   */
  value: Record<string, unknown>;
  /** Called with the next meta object whenever the editor changes. */
  onChange: (next: Record<string, unknown>) => void;
  /** Called with the editor's validity (false blocks the page Save). */
  onValidityChange: (valid: boolean) => void;
}

type Mode = "form" | "raw";

/** The Select entry that clears a single-value cosmetic. */
const NOT_SET = "";

function pretty(obj: Record<string, unknown>): string {
  return JSON.stringify(obj, null, 2);
}

/** True when `s` is a whole number (same rule as the stats editor's `int`). */
function isWholeNumber(s: string): boolean {
  const t = s.trim();
  if (t === "") return false;
  if (!/^[+-]?\d+$/.test(t)) return false;
  return Number.isSafeInteger(Number(t));
}

/** Scalar meta value coerced to the string its input shows. */
function toFieldString(raw: unknown): string {
  if (raw === undefined || raw === null) return "";
  if (typeof raw === "boolean") return raw ? "true" : "false";
  if (Array.isArray(raw)) return "";
  return String(raw);
}

/** Array meta value coerced to a chip list; non-arrays degrade to empty. */
function toFieldList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => (typeof v === "string" ? v : String(v)));
}

/** `string[]` and free-text `string` get the full card width. */
function spansBothColumns(f: MetaSchemaField): boolean {
  if (f.type === "string[]") return true;
  return f.type === "string" && (f.allowed ?? []).length === 0;
}

/**
 * Label for the chip "Add" button — the mockup words it after the field itself
 * ("Add badge" for `badges`), so a plural key drops its trailing "s". Endings
 * that are not a plural marker (`status`, `progress`, `axis`) are left alone;
 * this is a display nicety, not a general-purpose singulariser.
 */
export function addLabelFor(key: string): string {
  if (!key) return "value";
  if (/(ss|us|is)$/.test(key)) return key;
  if (key.endsWith("s")) return key.slice(0, -1);
  return key;
}

/**
 * Rebuild the meta object from the per-field editing state. Values are coerced
 * to their schema type; an `int` that is not a whole number round-trips as the
 * raw string so the invalid edit stays visible, exactly like the stats editor.
 * A cleared single-value field is emitted as JSON null — the server treats that
 * as "drop this cosmetic", which a shallow merge cannot express otherwise.
 */
function buildObject(
  schema: MetaSchemaField[],
  fields: Record<string, string>,
  lists: Record<string, string[]>,
): Record<string, unknown> {
  const obj: Record<string, unknown> = {};
  for (const f of schema) {
    if (f.type === "string[]") {
      obj[f.key] = lists[f.key] ?? [];
      continue;
    }
    const s = fields[f.key] ?? "";
    if (f.type === "int") {
      obj[f.key] = isWholeNumber(s) ? Number(s.trim()) : s;
    } else if (f.type === "bool") {
      obj[f.key] = s === "true";
    } else {
      obj[f.key] = s === NOT_SET && (f.allowed ?? []).length > 0 ? null : s;
    }
  }
  return obj;
}

/**
 * PlayerMetaEditor — schema-driven cosmetics editor, sibling of
 * PlayerStatsEditor. Ports docs/mockups (t-12) `player-detail-meta.html` §2
 * (Form mode), §3 (Raw JSON, empty schema, values outside the allow-list).
 *
 * Values that fall outside their allow-list are highlighted in warn colour and
 * never dropped silently: a schema can be narrowed after the fact, and quietly
 * deleting a player's badges would be worse than showing a rejected save.
 */
export function PlayerMetaEditor({
  schema,
  value,
  onChange,
  onValidityChange,
}: PlayerMetaEditorProps) {
  const hasSchema = schema.length > 0;
  const idPrefix = useId();

  const [mode, setMode] = useState<Mode>(hasSchema ? "form" : "raw");

  // Scalar fields (string / int / bool) are kept as strings so a partially
  // typed, still-invalid `int` survives a re-render.
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of schema) {
      if (f.type !== "string[]") init[f.key] = toFieldString(value[f.key]);
    }
    return init;
  });
  // `string[]` fields keep their own chip lists.
  const [lists, setLists] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    for (const f of schema) {
      if (f.type === "string[]") init[f.key] = toFieldList(value[f.key]);
    }
    return init;
  });

  const [rawText, setRawText] = useState<string>(() => pretty(value));

  const onValidityRef = useRef(onValidityChange);
  onValidityRef.current = onValidityChange;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Only a malformed `int` blocks Save. An out-of-allow-list value is a
  // warning, not an error — the server has the final word on it.
  const fieldErrors = useMemo(() => {
    const errs: Record<string, string | undefined> = {};
    for (const f of schema) {
      if (f.type === "int" && !isWholeNumber(fields[f.key] ?? "")) {
        errs[f.key] = "Must be a whole number.";
      }
    }
    return errs;
  }, [schema, fields]);

  const formValid = useMemo(
    () => Object.values(fieldErrors).every((e) => !e),
    [fieldErrors],
  );

  const rawParse = useMemo(() => {
    try {
      const parsed = JSON.parse(rawText) as unknown;
      if (
        parsed === null ||
        typeof parsed !== "object" ||
        Array.isArray(parsed)
      ) {
        return { error: "Meta must be a JSON object." };
      }
      return { value: parsed as Record<string, unknown> };
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [rawText]);

  const valid = mode === "form" ? formValid : !rawParse.error;

  useEffect(() => {
    onValidityRef.current(valid);
  }, [valid]);

  function emit(
    nextFields: Record<string, string>,
    nextLists: Record<string, string[]>,
  ) {
    setFields(nextFields);
    setLists(nextLists);
    onChangeRef.current(buildObject(schema, nextFields, nextLists));
  }

  function setField(key: string, raw: string) {
    emit({ ...fields, [key]: raw }, lists);
  }

  function setList(key: string, next: string[]) {
    emit(fields, { ...lists, [key]: next });
  }

  function handleRawChange(text: string) {
    setRawText(text);
    try {
      const parsed = JSON.parse(text) as unknown;
      if (
        parsed !== null &&
        typeof parsed === "object" &&
        !Array.isArray(parsed)
      ) {
        onChangeRef.current(parsed as Record<string, unknown>);
      }
    } catch {
      // Invalid JSON — don't emit; the validity effect surfaces the error.
    }
  }

  function switchMode(next: Mode) {
    if (next === mode) return;
    if (next === "raw") {
      setRawText(pretty(buildObject(schema, fields, lists)));
    } else if (!rawParse.error && rawParse.value) {
      // Re-hydrate the form from a cleanly parsing raw object. When the JSON
      // is invalid we keep the pre-switch field values and leave `rawText`
      // untouched, so nothing the admin typed is lost.
      const nextFields: Record<string, string> = {};
      const nextLists: Record<string, string[]> = {};
      for (const f of schema) {
        if (f.type === "string[]") {
          nextLists[f.key] = toFieldList(rawParse.value[f.key]);
        } else {
          nextFields[f.key] = toFieldString(rawParse.value[f.key]);
        }
      }
      setFields(nextFields);
      setLists(nextLists);
    }
    setMode(next);
  }

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <h3 className={styles.cardTitle}>Meta</h3>
          <span className={styles.cardSub}>
            {hasSchema && mode === "form"
              ? `players.meta · ${schema.length} ${
                  schema.length === 1 ? "field" : "fields"
                } from schema`
              : "players.meta · raw JSONB"}
          </span>
        </div>
        {hasSchema && (
          <div className={styles.segmented} role="group" aria-label="Meta edit mode">
            <button
              type="button"
              className={`${styles.seg} ${mode === "form" ? styles.segActive : ""}`}
              aria-pressed={mode === "form"}
              onClick={() => switchMode("form")}
            >
              Form
            </button>
            <button
              type="button"
              className={`${styles.seg} ${mode === "raw" ? styles.segActive : ""}`}
              aria-pressed={mode === "raw"}
              onClick={() => switchMode("raw")}
            >
              Raw JSON
            </button>
          </div>
        )}
      </div>

      <div className={styles.cardBody}>
        {mode === "form" && hasSchema && (
          <div className={styles.metaGrid}>
            {schema.map((field) => (
              <MetaField
                key={field.key}
                idPrefix={idPrefix}
                field={field}
                text={fields[field.key] ?? ""}
                list={lists[field.key] ?? []}
                error={fieldErrors[field.key]}
                onText={(v) => setField(field.key, v)}
                onList={(v) => setList(field.key, v)}
              />
            ))}
          </div>
        )}

        {mode === "raw" && (
          <>
            <div className={`${styles.alert} ${styles.alertInfo}`}>
              <Info size={14} aria-hidden className={styles.alertIcon} />
              {hasSchema ? (
                <div>
                  Raw mode edits the full <code className={styles.code}>meta</code>{" "}
                  object — including keys outside the schema. Saved values are
                  still validated against{" "}
                  <code className={styles.code}>meta_schema</code> on the server.
                </div>
              ) : (
                <div>
                  No <code className={styles.code}>meta_schema</code> defined for
                  this project — meta edits as raw JSON only. Add a schema in{" "}
                  <b>Config → Profile</b> to get a typed form.
                </div>
              )}
            </div>
            <textarea
              className={styles.jsonEditor}
              aria-label="Raw JSON"
              spellCheck={false}
              value={rawText}
              aria-invalid={rawParse.error ? true : undefined}
              onChange={(e) => handleRawChange(e.target.value)}
            />
            {rawParse.error ? (
              <div className={styles.fieldError}>
                <AlertCircle size={12} aria-hidden className={styles.errIcon} />
                {rawParse.error}
              </div>
            ) : (
              <div className={styles.helper}>
                Must be a valid JSON object. Invalid JSON blocks Save with an
                inline parser error.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// === One schema-driven control ======================================= //

/** What a single control needs to render and report changes. */
interface MetaControlProps {
  fieldId: string;
  /** id of the inline error node, for aria-describedby. */
  errId?: string;
  field: MetaSchemaField;
  text: string;
  list: string[];
  error?: string;
  onText: (v: string) => void;
  onList: (v: string[]) => void;
}

type MetaFieldProps = Omit<MetaControlProps, "fieldId" | "errId"> & {
  idPrefix: string;
};

function MetaField({
  idPrefix,
  field,
  text,
  list,
  error,
  onText,
  onList,
}: MetaFieldProps) {
  const fieldId = `${idPrefix}-${field.key}`;
  const errId = `${fieldId}-err`;
  const allowed = field.allowed ?? [];
  const hasAllowList = allowed.length > 0;
  // A chip list is a composite widget, not one labelable control: pointing the
  // <label> at its "Add" button would rename that button after the whole field.
  const isComposite = field.type === "string[]";

  return (
    <div
      className={`${styles.metaField} ${
        spansBothColumns(field) ? styles.span2 : ""
      }`}
    >
      <label htmlFor={isComposite ? undefined : fieldId}>
        <span className={styles.sk}>{field.key}</span>
        <span className={styles.typeTag}>{field.type}</span>
        {hasAllowList && <span className={styles.lockTag}>allow-list</span>}
      </label>

      <MetaControl
        fieldId={fieldId}
        errId={error ? errId : undefined}
        field={field}
        text={text}
        list={list}
        error={error}
        onText={onText}
        onList={onList}
      />

      {error && (
        <div className={styles.fieldError} id={errId}>
          <AlertCircle size={12} aria-hidden className={styles.errIcon} />
          {error}
        </div>
      )}
    </div>
  );
}

function MetaControl({
  fieldId,
  errId,
  field,
  text,
  list,
  error,
  onText,
  onList,
}: MetaControlProps) {
  const allowed = field.allowed ?? [];
  const hasAllowList = allowed.length > 0;

  switch (field.type as MetaFieldType) {
    case "bool": {
      const on = text === "true";
      return (
        <div className={styles.boolRow}>
          <button
            type="button"
            id={fieldId}
            role="switch"
            aria-checked={on}
            aria-label={field.key}
            className={`${styles.toggle} ${on ? styles.toggleOn : ""}`}
            onClick={() => onText(on ? "false" : "true")}
          >
            <span className={styles.knob} />
          </button>
          <span
            className={`${styles.boolVal} ${on ? styles.boolValOn : ""}`}
          >
            {on ? "true" : "false"}
          </span>
        </div>
      );
    }

    case "string[]":
      return (
        <ChipList
          field={field}
          list={list}
          onList={onList}
          inputId={fieldId}
        />
      );

    case "string":
      if (hasAllowList) {
        const orphan = text !== "" && !allowed.includes(text);
        return (
          <>
            <Select
              id={fieldId}
              value={text}
              onChange={onText}
              options={[
                { value: NOT_SET, label: "— not set —" },
                ...allowed.map((a) => ({ value: a, label: a })),
                // Keep an out-of-list value selectable so opening the menu
                // does not silently rewrite it.
                ...(orphan ? [{ value: text, label: text }] : []),
              ]}
              placeholder="— not set —"
              monospaceValue
              className={orphan ? styles.selectWarn : undefined}
            />
            {orphan && (
              <div className={styles.warnHelper}>
                Current value is not in the allow-list. Pick an allowed one to
                save.
              </div>
            )}
          </>
        );
      }
      return (
        <>
          <input
            id={fieldId}
            type="text"
            className={styles.input}
            value={text}
            onChange={(e) => onText(e.target.value)}
          />
          <div className={styles.helperTight}>
            No allow-list defined — any string is accepted.
          </div>
        </>
      );

    default:
      // int
      return (
        <input
          id={fieldId}
          type="text"
          className={`${styles.input} ${styles.mono} ${
            error ? styles.invalid : ""
          }`}
          value={text}
          aria-invalid={error ? true : undefined}
          aria-describedby={errId}
          onChange={(e) => onText(e.target.value)}
        />
      );
  }
}

// === string[] control ================================================ //

interface ChipListProps {
  field: MetaSchemaField;
  list: string[];
  onList: (next: string[]) => void;
  inputId: string;
}

/**
 * Chips for a `string[]` cosmetic. With an allow-list the "Add" button opens a
 * menu of the values not yet picked; without one it is a free-text input, the
 * same interaction `allowed_avatars` uses in the project config.
 */
function ChipList({ field, list, onList, inputId }: ChipListProps) {
  const [draft, setDraft] = useState("");
  const allowed = field.allowed ?? [];
  const hasAllowList = allowed.length > 0;
  const remaining = allowed.filter((a) => !list.includes(a));
  const orphans = hasAllowList ? list.filter((v) => !allowed.includes(v)) : [];
  const noun = addLabelFor(field.key);

  function remove(idx: number) {
    onList(list.slice(0, idx).concat(list.slice(idx + 1)));
  }

  function addDraft() {
    const chip = draft.trim();
    if (!chip || list.includes(chip)) return;
    onList([...list, chip]);
    setDraft("");
  }

  return (
    <>
      <div className={styles.chipWrap} role="group" aria-label={field.key}>
        {list.map((chip, i) => {
          const orphan = hasAllowList && !allowed.includes(chip);
          return (
            <span
              key={`${chip}-${i}`}
              className={`${styles.chip} ${orphan ? styles.chipWarn : ""}`}
            >
              {chip}
              <button
                type="button"
                className={styles.chipX}
                aria-label={`Remove ${chip}`}
                onClick={() => remove(i)}
              >
                ×
              </button>
            </span>
          );
        })}

        {hasAllowList ? (
          <Menu
            aria-label={`Allowed values for ${field.key}`}
            placement="bottom-start"
            items={remaining.map((a) => ({
              label: a,
              onSelect: () => onList([...list, a]),
            }))}
            trigger={({ toggle, ref }) => (
              <button
                type="button"
                id={inputId}
                ref={ref}
                className={styles.chipAdd}
                disabled={remaining.length === 0}
                onClick={toggle}
              >
                <Plus size={11} aria-hidden />
                Add {noun}
              </button>
            )}
          />
        ) : (
          <input
            id={inputId}
            type="text"
            className={styles.chipInput}
            aria-label={`Add a value to ${field.key}`}
            placeholder={list.length === 0 ? "Add value…" : undefined}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={addDraft}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") {
                e.preventDefault();
                addDraft();
              }
            }}
          />
        )}
      </div>

      {orphans.length > 0 ? (
        <div className={styles.warnHelper}>
          {orphans.length}{" "}
          {orphans.length === 1 ? "value is" : "values are"} outside the
          allow-list — highlighted, never removed automatically.
        </div>
      ) : hasAllowList ? (
        <div className={styles.helperTight}>
          {list.length} of {allowed.length} available · “Add {noun}” opens a menu
          of the remaining allow-list values.
        </div>
      ) : (
        <div className={styles.helperTight}>
          No allow-list defined — any string is accepted.
        </div>
      )}
    </>
  );
}
